import {
  alpha,
  Box,
  Chip,
  makeStyles,
  Typography,
  useTheme,
} from '@material-ui/core';

import {
  CHANNEL_ICON_CONFIG,
  UNKNOWN_CHANNEL_CONFIG,
} from 'constants/channel-icon-config';

const getChannelConfig = (type?: keyof typeof CHANNEL_ICON_CONFIG) => {
  return type && CHANNEL_ICON_CONFIG[type]
    ? CHANNEL_ICON_CONFIG[type]
    : UNKNOWN_CHANNEL_CONFIG;
};

const useStyles = makeStyles(() => ({
  chip: ({ color, forceColor }: { color: string; forceColor: boolean }) => ({
    backgroundColor: 'transparent',
    border: '1px solid',
    borderColor: alpha(color, 0.9),
    fontSize: 8,
    fontWeight: 500,
    color,
    padding: 2,
    height: 20,
    maxWidth: '100%',

    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      paddingRight: 4,
    },
    '& .MuiChip-icon': {
      flexShrink: 0,
      marginLeft: 4,
      width: 8,
      height: 8,
      color: 'inherit',
      ...(forceColor && {
        '& path': {
          fill: 'currentColor',
        },
      }),
    },
  }),
  svgIcon: ({ forceColor }: { forceColor: boolean }) => ({
    ...(forceColor && {
      '& path': {
        fill: 'currentColor',
      },
    }),
  }),
}));

interface StyledChannelChipIconProps {
  type?: keyof typeof CHANNEL_ICON_CONFIG;
  label?: string;
  variant?: 'container' | 'text';
  color?: string;
}

function StyledChannelChipIcon({
  variant = 'container',
  label,
  type,
  color: colorProp,
}: StyledChannelChipIconProps) {
  const { palette } = useTheme();

  const config = getChannelConfig(type);

  let color = colorProp;
  if (!color) {
    if (type === 'WHATSAPP') {
      color = palette.channelBadges.whatsapp;
    } else if (type === 'WHATSAPP_OFICIAL') {
      color = palette.channelBadges.whatsappOfficial;
    } else if (type === 'INSTAGRAM') {
      color = palette.channelBadges.instagram;
    } else if (type === 'MESSENGER') {
      color = palette.channelBadges.messenger;
    } else if (type === 'TELEGRAM') {
      color = palette.channelBadges.telegram;
    } else {
      color = palette.channelBadges.unknown;
    }
  } else {
    const normalized = color.trim().toUpperCase();
    if (normalized === '#E93F66') {
      color = palette.channelBadges.offline;
    }
  }

  const classes = useStyles({
    color,
    forceColor: !!colorProp || palette.type === 'dark',
  });
  const Icon = config.iconVariant;

  if (variant === 'text') {
    return (
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Icon
          style={{
            color: palette.text.primary,
            flexShrink: 0,
          }}
          className={classes.svgIcon}
        />
        <Typography
          style={{ fontSize: 10, fontWeight: 500 }}
          color="textPrimary"
        >
          {label || config.label || ''}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Chip
        label={label || config.label || ''}
        icon={<Icon style={{ width: 10, height: 10, color: 'inherit' }} />}
        className={classes.chip}
      />
    </Box>
  );
}

export default StyledChannelChipIcon;
